/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-02 16:54:08
 **/

import { PropsWithChildren, useMemo } from "react";
import LottiePlayer from "@rsupport/lottieplayer";

import { Maybe } from "@/frameworks/libraries/components";
import { Button, Row, Tooltip, useToast } from "@/frameworks/designStuff/components";
import { ChronoUtils, Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";

import { colors } from "@/frameworks/core/styles";
import { PhoneCallAtoms, usePhoneCall } from "@/frameworks/core/hooks";

import type { CommandsProps } from "./CommandBar";
import { useAtomValue } from "jotai";
import { JotaiStore } from "@/frameworks/libraries";

const LOGTAG = "#Commands#";

export default function Commands({
	callID,
	callStatus,
	lottieOptions,
	overCallees,
	isGroupCallHistory,
	callerID,
	onAddUser
}: PropsWithChildren<CommandsProps>) {
	const phoneCall = usePhoneCall();
	const toast = useToast();
	const callUsers = phoneCall.state.users(callID)[0];
	const participatedUser = phoneCall.state.participatedUserList(callID);
	const myMedia = useMemo(
		() => callUsers?.[Configure.userID].media ?? phoneCall.default_user_media_info,
		[callUsers]
	);
	const recordTime = useAtomValue(PhoneCallAtoms.RecordTime(callID ?? ""), { store: JotaiStore });

	return (
		<Row gap={8}>
			<li>
				<Button.Icon
					name={myMedia.video === "on" ? "CamOn24" : "CamOff24"}
					size="Xlarge"
					onClick={() => {
						phoneCall.camera_update(callID, Configure.userID, myMedia.video === "on" ? "off" : "on");
					}}
					filled={myMedia.video === "on" ? "neutral_secondary" : "secondary"}
					style={{ border: `1px solid ${colors.neutral_50_20}` }}
				/>
			</li>
			<li>
				<Button.Icon
					name={myMedia.audio === "on" ? "MicOn24" : "MicOff24"}
					size="Xlarge"
					onClick={() => {
						phoneCall.microphone_update(callID, Configure.userID, myMedia.audio === "on" ? "off" : "on");
					}}
					filled={myMedia.audio === "on" ? "neutral_secondary" : "secondary"}
					style={{ border: `1px solid ${colors.neutral_50_20}` }}
				/>
			</li>
			<li>
				<Button
					filled={
						callStatus !== "ONGOING"
							? "transparent"
							: !myMedia.isRecording
								? "neutral_secondary"
								: "secondary"
					}
					disabled={callStatus !== "ONGOING"}
					onClick={() => {
						if (myMedia?.isRecording) {
							phoneCall.record_update(callID, "stop");
							toast.show({
								type: "system",
								message: _T("통화 녹음이 종료되었습니다. 저장된 파일은 파일함에서 확인 가능합니다.")
							});
						} else {
							phoneCall.record_update(callID, "start");
							toast.show({ type: "system", message: _T("통화 녹음이 시작되었습니다.") });
						}
					}}
					style={{
						border: `1px solid ${colors.neutral_50_20}`,
						borderRadius: "100%",
						width: 36,
						height: 36,
						opacity: callStatus !== "ONGOING" ? 0.4 : 1
					}}
				>
					{myMedia?.isRecording ? (
						<Tooltip
							message={_T("녹음중 {{time}}", {
								time: ChronoUtils.calculateRecording(recordTime ?? 0)
							})}
							hasIcon={false}
						>
							<LottiePlayer
								isStopped={!myMedia?.isRecording}
								isPaused={!myMedia?.isRecording}
								options={lottieOptions}
								height={24}
								width={24}
							/>
						</Tooltip>
					) : (
						<LottiePlayer
							isStopped={!myMedia?.isRecording}
							isPaused={!myMedia?.isRecording}
							options={lottieOptions}
							height={24}
							width={24}
						/>
					)}
				</Button>
			</li>
			<li>
				<Maybe test={callStatus === "ONGOING" || callStatus === "READY"}>
					<Button.Icon
						name="ScreenShare24"
						// filled={myMedia.screenShare ? "secondary" : "neutral_secondary"}
						filled={
							callStatus !== "ONGOING"
								? "transparent"
								: callUsers?.[Configure.userID]?.status !== "ACCEPTED"
									? "neutral_secondary"
									: "secondary"
						}
						size="Xlarge"
						disabled={callUsers?.[Configure.userID]?.status !== "ACCEPTED"}
						onClick={() => {
							phoneCall.screen_update(callID, Configure.userID, "start");
						}}
						style={{ border: `1px solid ${colors.neutral_50_20}` }}
					/>
				</Maybe>
			</li>
			{!isGroupCallHistory && (
				<li>
					<Button.Icon
						name="CallAdd24"
						filled={overCallees ? "transparent" : "neutral"}
						size="Xlarge"
						disabled={callStatus === "READY" || participatedUser.length === 4}
						onClick={onAddUser}
						style={{ border: `1px solid ${colors.neutral_50_20}` }}
					/>
				</li>
			)}
		</Row>
	);
}
